import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModuleNames } from 'projects/Autofacets-Core/src/app/utils/enums';
import { UserDataService } from 'src/app/DataServices/user-data.service';
import { ErrorLogAnalysisReportService } from '../../../../business-services/error-log-analysis-report.service';
import { ErrorLogAnalysisModel, ErrorLogStatusList } from '../../../../business-entities/error-log-analysis-report.model';
import { getStatusOption, getStatusUpdatedText } from '../error-log-analysis.helper';

/**
 * Status of one spike. Plain text by default (the report listing). With `editable` and edit rights it is a dropdown
 * plus an "Update Status" button: the status is saved only when the button is clicked. Emits `saved` so the parent can reload.
 */
@Component({
  selector: 'app-error-log-status-editor',
  templateUrl: './error-log-status-editor.component.html',
  styleUrls: ['../error-log-shared.css']
})
export class ErrorLogStatusEditorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() item: ErrorLogAnalysisModel;
  @Input() editable: boolean = false;
  @Output() saved = new EventEmitter<void>();

  readonly statusList = ErrorLogStatusList;
  readonly getStatusOption = getStatusOption;
  hasEditRight: boolean = false;
  // Status picked in the dropdown, not saved until "Update Status" is clicked.
  selectedStatus: number = null;
  private userSub: Subscription;

  constructor(
    private service: ErrorLogAnalysisReportService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private userDataService: UserDataService
  ) { }

  ngOnInit(): void {
    this.userSub = this.userDataService.currentUserSubject.subscribe((res) => {
      this.hasEditRight = !!res?.userRoleMenuDetails?.find(x => x.moduleId == ModuleNames.ErrorLogAnalysisReport)?.canEdit;
    });
  }

  // A different spike (or a reloaded one) resets the dropdown to its saved status.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item) {
      this.selectedStatus = this.item?.status ?? null;
    }
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  get canChange(): boolean {
    return this.editable && this.hasEditRight;
  }

  get status() {
    return getStatusOption(this.item.status);
  }

  get updatedText(): string {
    return getStatusUpdatedText(this.item);
  }

  async onUpdateStatus() {
    if (this.selectedStatus === this.item.status) {
      return;
    }
    this.spinner.show();
    let isSaved = await this.service.UpdateSpikeStatus(this.item, this.selectedStatus);
    this.spinner.hide();
    if (isSaved) {
      this.toastr.success(this.translate.instant('lblStatusUpdatedSuccessfully'));
      this.saved.emit();
    } else {
      this.selectedStatus = this.item.status;
      this.toastr.error(this.translate.instant('lblStatusUpdateFailed'));
    }
  }
}
